import Taxes from '../Taxes';
import roundValue from '../../roundValue';
import { ICurrency, ITaxes } from '../interfaces';
import { CalculationPayInType } from '../enums';

export class StsTaxes extends Taxes {
    private payoutTaxThresholdAmount = 0;

    public configure(taxes: ITaxes, currency: ICurrency): void {
        super.configure(taxes, currency);

        if (taxes.payout?.rates?.length) {
            const firstPayoutTaxRate = taxes.payout?.rates.find((tax) => Number(tax.rate));

            if (!firstPayoutTaxRate) {
                // disable payout tax
                this.payoutTaxThresholdAmount = Infinity;
                return;
            }

            this.payoutTaxThresholdAmount = Number(firstPayoutTaxRate.from);
        }
    }

    private payoutPrecision(amount: number): number {
        if (amount < this.payoutTaxThresholdAmount) {
            return this.currency.precision;
        }

        return 0;
    }

    private roundFloor(amount: number): number {
        return Math.floor(amount * 10 ** this.currency.precision) / 10 ** this.currency.precision;
    }

    private getWinTaxPPRaw(amount: number, totalOdd: number, runsCount: number): number {
        const { payoutTax, winAmountAfterPayin } = this.getPayoutTaxesWithWin(amount, totalOdd);

        const winTaxPP = roundValue(
            payoutTax * runsCount,
            this.payoutPrecision(winAmountAfterPayin),
        );

        return winTaxPP;
    }

    private getPayoutTaxesWithWin(
        amount: number,
        totalOdd: number,
    ): { payoutTax: number; winAmountAfterPayin: number } {
        const winAmountAfterPayin = this.getWinAfterPayinRaw(amount, 1, totalOdd);

        const payoutTax = this.getPayoutTaxesRaw(
            roundValue(winAmountAfterPayin, this.payoutPrecision(amount)),
        );

        return {
            payoutTax,
            winAmountAfterPayin,
        };
    }

    public getWinTaxPP(amount: number, totalOdd: number, runsCount: number): string {
        const winTaxPP = this.getWinTaxPPRaw(amount, totalOdd, runsCount);

        return this.format(winTaxPP);
    }

    public getPossibleWinRaw(amount: number, runsCount: number, totalOdd: number): number {
        const amountAfterPayin = this.getAmountAfterPayin(amount);
        const payoutTax = this.getTaxesAmount(amount, totalOdd);

        const payoutTaxRounded = roundValue(payoutTax, this.payoutPrecision(amountAfterPayin));

        return this.roundFloor(amountAfterPayin * totalOdd - payoutTaxRounded * runsCount);
    }

    public getPossibleWin(amount: number, runsCount: number, totalOdd: number): string {
        const possibleWinRaw = this.getPossibleWinRaw(amount, runsCount, totalOdd);
        return this.format(possibleWinRaw);
    }

    public getTaxesAmount(amount: number, totalOdd: number): number {
        const { payoutTax, winAmountAfterPayin } = this.getPayoutTaxesWithWin(amount, totalOdd);

        return roundValue(payoutTax, this.payoutPrecision(winAmountAfterPayin));
    }

    public getWinAfterPayinRaw(amount: number, runsCount: number, totalOdd: number): number {
        const amountAfterPayin = this.getAmountAfterPayin(amount);

        return this.roundFloor(amountAfterPayin * totalOdd * runsCount);
    }

    public getWinAfterPayin(amount: number, runsCount: number, totalOdd: number): string {
        const amountAfterPayinRaw = this.getWinAfterPayinRaw(amount, runsCount, totalOdd);

        return this.format(amountAfterPayinRaw);
    }

    public getAmountAfterPayin(amount: number): number {
        return amount - this.getPayinTaxAmountRaw(amount, 1);
    }

    public getPayinTaxAmountRaw(amount: number, runsCount: number): number {
        const payinRate = this.getPayinRate(amount);

        let payinTaxAmount;

        if (this.taxes.calculationPayInType === CalculationPayInType.INCLUSIVE) {
            payinTaxAmount = (payinRate * amount) / (payinRate + 100);
        } else {
            payinTaxAmount = (payinRate / 100) * amount;
        }

        return payinTaxAmount * runsCount;
    }

    public getPayoutTaxesRaw(winAmount: number): number {
        if (!this.taxes) {
            return 0;
        }

        const rate = this.getPayoutRate(winAmount);
        const winAmountRounded = roundValue(winAmount, 0);
        return (rate / 100) * winAmountRounded;
    }
}
